<script lang="ts" setup>
import Card from '#components/elements/card/Card.vue';
import CardLink from '#components/elements/cardLink/CardLink.vue';
import IndicatorGrid from '#components/indicators/IndicatorGrid.vue';
import IndicatorRow from '#components/indicators/IndicatorRow.vue';
import IndicatorWidget from '#components/indicators/IndicatorWidget.vue';
import LayoutPerformance from '#components/layouts/LayoutPerformance/LayoutPerformance.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useIndicators } from '#composables/useIndicators/useIndicators';
import { useLanguage } from '#composables/useLanguage';
import { useMarketShareDashboardLink } from '#composables/useMarketShareDashboardLink';
import { usePermissions } from '#composables/usePermissions';
import KpiDataTable from '#domain/performance/kpi/components/KpiDataTable/KpiDataTable.vue';
import { routes } from '#router/routes';
import { useUserStore } from '#stores/user.store';
import type { IndicatorName } from '@carrefour-gcs/shared';
import { computed } from 'vue';

const { t } = useLanguage();
const userStore = useUserStore();

const { marketShareLink } = useMarketShareDashboardLink();
const {
  taxonomy: { taxonomy }
} = useKpisPerimeter();

const {
  hasAccessToBusinessEquationAllCustomers,
  hasAccessToBusinessEquationCardholdersNonCardholders,
  hasAccessToPossessionZoomPage,
  hasAccessToDnDvZoomPage,
  hasAccessToCompetitorVision,
  hasAccessToSalesPossessionRate,
  hasAccessToStoreStock,
  hasAccessToTotalStock,
  hasAccessToWarehouseStock,
  hasAccessToGrossServiceRate
} = usePermissions();

const indicators = computed(() => {
  const indicators: Array<IndicatorName> = [
    'salesAmount',
    'salesQuantity',
    'supplierMarketShareAmount',
    'supplierMarketShareQuantity',
    'nielsenMarketShareSalesAmount',
    'nielsenMarketShareSalesQuantity',
    'numericalDistribution',
    'weightedDistribution',
    'serviceRateUpstream',
    'serviceRateDownstream',
    'storeStock',
    'warehouseStock',
    'totalStock',
    'salesPossessionRate'
  ];

  if (hasAccessToBusinessEquationCardholdersNonCardholders.value) {
    indicators.push('numberOfCardholders');
    indicators.push('averageBasketAmountCardholders');
  } else if (hasAccessToBusinessEquationAllCustomers.value) {
    indicators.push('numberOfTransactions');
    indicators.push('averageBasketAmount');
  }

  return indicators;
});

const data = useIndicators(indicators.value);

const possessionRateDetailLink = computed(() =>
  hasAccessToPossessionZoomPage.value && taxonomy.value === 'carrefour'
    ? routes.detail('possession-rate').path
    : undefined
);

const dnDvDetailLink = computed(() =>
  hasAccessToDnDvZoomPage.value && taxonomy.value === 'carrefour' ? routes.detail('dn-dv').path : undefined
);

const coraMatchScorecardNotif = computed(() =>
  userStore.userCountry === 'FRA' ? t('performanceCarrefourCatman.coraMatchNotice') : undefined
);
const coraMatchStockScorecardNotif = computed(() =>
  userStore.userCountry === 'FRA' ? t('performanceCarrefourCatman.stock.coraMatchNotice') : undefined
);

const coraMatchDatatableNotif = computed(() =>
  userStore.userCountry === 'FRA' ? t('dataTablePerformance.coraMatchNotice') : undefined
);

const displayMarketShareLink = computed(
  () => taxonomy.value === 'nielsen' && marketShareLink.value && { to: marketShareLink.value }
);

const displayStockSection = computed(
  () => hasAccessToStoreStock.value || hasAccessToTotalStock.value || hasAccessToWarehouseStock.value
);
</script>

<template>
  <!-- KPI Score Cards -->
  <LayoutPerformance :title="t('performanceCarrefourCatman.title')">
    <section class="space-y-6 h-full">
      <!-- Sales Amount/Quantity and Market Share -->
      <IndicatorRow :title="t('performanceCarrefourCatman.subtitleSales')">
        <Card :to="routes.detail('sales-amount').path">
          <IndicatorWidget v-bind="data.salesAmount" type="breakdown" />
        </Card>
        <Card :to="routes.detail('sales-quantity').path">
          <IndicatorWidget v-bind="data.salesQuantity" type="breakdown" />
        </Card>
        <Card vertical divide class="w-full">
          <IndicatorWidget v-bind="data.supplierMarketShareAmount" />
          <IndicatorWidget v-bind="data.supplierMarketShareQuantity" />
        </Card>
        <Card v-bind="displayMarketShareLink" vertical divide class="w-full">
          <IndicatorWidget v-bind="data.nielsenMarketShareSalesAmount" />
          <IndicatorWidget v-bind="data.nielsenMarketShareSalesQuantity" />
        </Card>
      </IndicatorRow>

      <!-- Transactions and Average Basket amount -->
      <IndicatorRow>
        <Card>
          <IndicatorWidget v-bind="data.numberOfTransactions" />
        </Card>
        <Card>
          <IndicatorWidget v-bind="data.averageBasketAmount" />
        </Card>

        <Card>
          <IndicatorWidget v-bind="data.numberOfCardholders" />
        </Card>
        <Card>
          <IndicatorWidget v-bind="data.averageBasketAmountCardholders" />
        </Card>

        <div
          v-if="hasAccessToBusinessEquationAllCustomers || hasAccessToBusinessEquationCardholdersNonCardholders"
          class="flex w-full"
        >
          <CardLink
            :disabled="taxonomy === 'nielsen'"
            :to="routes.businessEquation.path"
            :label="t('businessEquationZoom.title')"
          />
        </div>
      </IndicatorRow>

      <IndicatorRow :title="t('performanceCarrefourCatman.subtitlePossessionAndDistribution')">
        <!--  Possession Rate -->
        <IndicatorRow v-if="hasAccessToSalesPossessionRate">
          <Card :to="possessionRateDetailLink">
            <IndicatorWidget v-bind="data.salesPossessionRate" :notif="coraMatchScorecardNotif" />
          </Card>
        </IndicatorRow>

        <!--  Numerical/Weighted Distribution -->
        <IndicatorRow>
          <Card :to="dnDvDetailLink">
            <IndicatorWidget v-bind="data.numericalDistribution" />
          </Card>
          <Card :to="dnDvDetailLink">
            <IndicatorWidget v-bind="data.weightedDistribution" />
          </Card>
        </IndicatorRow>
      </IndicatorRow>

      <IndicatorGrid
        v-if="hasAccessToGrossServiceRate"
        :columns="3"
        :title="t('performanceCarrefourCatman.subtitleServiceRate')"
      >
        <Card class="col-span-2" divide>
          <IndicatorWidget v-bind="data.serviceRateUpstream" type="breakdown" :notif="coraMatchScorecardNotif" />
          <IndicatorWidget v-bind="data.serviceRateDownstream" type="breakdown" :notif="coraMatchScorecardNotif" />
        </Card>
      </IndicatorGrid>

      <IndicatorRow v-if="displayStockSection" :title="t('performanceCarrefourCatman.subtitleStock')">
        <Card divide>
          <IndicatorWidget
            v-if="hasAccessToWarehouseStock"
            v-bind="data.warehouseStock"
            type="breakdown"
            :notif="coraMatchStockScorecardNotif"
          />
          <IndicatorWidget
            v-if="hasAccessToStoreStock"
            v-bind="data.storeStock"
            type="breakdown"
            :notif="coraMatchStockScorecardNotif"
          />
          <IndicatorWidget
            v-if="hasAccessToTotalStock"
            v-bind="data.totalStock"
            type="breakdown"
            :notif="coraMatchStockScorecardNotif"
          />
        </Card>
      </IndicatorRow>

      <div v-if="hasAccessToCompetitorVision" class="flex w-full">
        <CardLink :to="routes.competitor.vision.path" :label="t('competitor.vision.title')" />
      </div>
    </section>

    <KpiDataTable data-table-view="performance" :is-export-enabled="true" :notif="coraMatchDatatableNotif" />
  </LayoutPerformance>
</template>
